// ============================================================
// PLZ Company — EDITABLE CONTENT CONFIG
// All site copy lives here. Edit text freely; layout never changes.
// Images are bundled at build time so they can never 404.
// ============================================================

import hacksterImg from "@/assets/hackster.jpg";
import waveTherapyImg from "@/assets/wavetherapy.jpg";
import mindMovesImg from "@/assets/mindmoves-meditation.jpg";
import mindMovesLogo from "@/assets/mindmoves-logo.png";
import foundersAiImg from "@/assets/paul-laura.jpg";
import foundersRealImg from "@/assets/paul-laura-real.jpg";

const content = {
  brand: {
    name: "PLZ Company",
    mark: "P",
    tagline: "We deliver PLZ'ing work",
  },

  nav: [
    { label: "The Problem", target: "#problem" },
    { label: "What We Do", target: "#what-we-do" },
    { label: "Who We Serve", target: "#who-we-serve" },
    { label: "AI Portfolio", target: "#portfolio" },
    { label: "Why PLZ", target: "#why-plz" },
  ],
  navCta: { label: "Book a Free Consultation", target: "#contact" },

  hero: {
    eyebrow: "PLZ Company — We deliver PLZ'ing work",
    titleLines: [
      { text: "Building good,", accent: false },
      { text: "healthy solutions", accent: false },
      { text: "with AI.", accent: true },
    ],
    subheadline:
      "We help solopreneurs, healthpreneurs, and growing brands put AI to work the right way — clearing the busywork so you can focus on strategy, brand, and the people who matter.",
    primaryCta: { label: "Explore Our AI Portfolio", target: "#portfolio" },
    secondaryCta: { label: "See How We Help", target: "#problem" },
  },

  marquee: [
    "AI Strategy & Audits",
    "Social Media Automation",
    "Done-For-You AI Tooling",
    "Content & Video Workflows",
    "Marketing Automation",
    "WaveTherapy.ai",
    "MindMoves.ai",
    "Hackster.ai",
    "PLZ'ing Solutions",
  ],

  chapters: {
    problem: {
      id: "problem",
      num: "01",
      label: "The Problem We Solve",
      headlineA: "Ten hats, one body — ",
      headlineAccent: "let us help you protect it all.",
      paragraphs: [
        "Marketer, content creator, customer service rep, social media manager — and, if you're lucky, the person who actually delivers your product or service.",
        "AI can take the tedious work off your plate, and put personalized guidance in more hands than ever. Most people just don't have time to figure out which tools actually deliver.",
      ],
      closing: "That's where we come in.",
    },

    whatWeDo: {
      id: "what-we-do",
      num: "02",
      label: "What We Do",
      headlineA: "Two sides of one mission: AI removes the friction — ",
      headlineAccent: "humans keep it meaningful.",
      sides: [
        {
          eyebrow: "For Your Business",
          title: "Done-For-You AI Tool Setup",
          body: "We assess your business, recommend the right AI marketing and social tools, and build the workflows — content calendars, social scheduling, email sequences, customer follow-up, and more.",
          cta: { label: "View Services", target: "#contact" },
        },
        {
          eyebrow: "For Your Life",
          title: "Our AI Portfolio",
          body: "A growing suite of our own AI-powered health and wellness products — personalized guidance, plans, and support that used to require a small army of coaches and specialists.",
          cta: { label: "Browse the Portfolio", target: "#portfolio" },
        },
      ],
    },

    whoWeServe: {
      id: "who-we-serve",
      num: "03",
      label: "Who We Serve",
      segments: [
        {
          num: "01",
          title: "Solopreneurs",
          desc: "The leverage of a full team — without the payroll.",
        },
        {
          num: "02",
          title: "Healthpreneurs",
          desc: "Practitioners and coaches who market like a business while practicing like professionals.",
        },
        {
          num: "03",
          title: "Small and mid-size brands",
          desc: "Growing brands ready to modernize marketing and amplify their voice.",
        },
        {
          num: "04",
          title: "Families & Athletes",
          desc: "More personal ways to improve health and performance with our AI Portfolio of solutions.",
        },
      ],
    },

    whyPlz: {
      id: "why-plz",
      num: "04",
      label: "Why PLZ",
      headlineA: "A family-run boutique agency. ",
      headlineAccent: "A husband-and-wife team",
      headlineB: " who tests every tool on our projects first.",
      paragraphs: [
        "We're Paul and Laura Zook. We've spent our careers in marketing, content, and technology — and we run these tools in our own business and our own lives before we ever bring them to you.",
        "We're building the businesses and products we'd want for our own family to thrive because that's what the personal health revolution should look like.",
      ],
      cta: { label: "Meet Paul & Laura", target: "#contact" },
      compare: {
        realSrc: foundersRealImg,
        aiSrc: foundersAiImg,
        alt: "Paul & Laura Zook — drag the handle to compare the real photo with our AI portrait",
        caption: "Paul & Laura Zook — Founders",
        hint: "Drag to compare — the real us vs. our AI portrait",
        realLabel: "Real",
        aiLabel: "AI",
      },
    },
  },

  portfolio: {
    id: "portfolio",
    label: "The AI Portfolio",
    headlineA: "Our own products, ",
    headlineAccent: "battle-tested first.",
    intro:
      "Every tool and workflow we recommend is one we run ourselves. These are the products we build, break, and improve before they ever reach your business.",
    items: [
      {
        name: "WaveTherapy.ai",
        tag: "Live in Production",
        tagline: "Recovery, personalized",
        desc: "AI-guided recovery and wellness plans that adapt to your body, your schedule, and your goals.",
        image: waveTherapyImg,
        imageAlt: "The Wave Therapy machine with electrodes and accessories — WaveTherapy.ai",
        url: "https://wavetherapy.ai",
      },
      {
        name: "MindMoves.ai",
        tag: "In the lab",
        tagline: "Mental fitness, daily",
        desc: "Personalized mindset coaching for aspiring college/pro athletes that understand your MindMoves you to accomplish big dreams. Train your brain like you train your body.",
        image: mindMovesImg,
        imageAlt: "Woman meditating at sunrise — MindMoves.ai",
        logoOverlay: mindMovesLogo,
        url: "https://mindmoves.ai",
      },
      {
        name: "Hackster.ai",
        tag: "Beta",
        tagline: "Your biohacking buddy",
        desc: "Take a 2-minute assessment and get an AI-personalized Hackster Stack — supplements, devices, and a matched health coach for your top goals.",
        image: hacksterImg,
        imageAlt: "Hackster.ai app — Optimize your health with biohacking excellence",
        url: "https://hackster.ai",
      },
    ],
  },

  testimonial: {
    label: "Client Spotlight",
    quote:
      "Working with Paul & Laura transformed our entire digital acquisition pipeline. Their AI-driven social automation scaled our engagement by 340% while keeping our brand voice 100% authentic. PLZ Company is the most cost-effective secret weapon in tech.",
    author: "Craig Short",
    initials: "CS",
    role: "VP of Brand & Growth, ProBaller Ultra Premium Energy Drink",
  },

  contact: {
    id: "contact",
    label: "Say Hello",
    headlineA: "Let AI handle the busywork. ",
    headlineAccent: "You handle the vision",
    headlineB: " — for your business, and your health.",
    intro:
      "Tell us a little about you and what you're building. We read every message ourselves and reply within one business day.",
    fields: {
      name: "Your name",
      email: "Email address",
      company: "Company / organization (optional)",
      message: "How can we help?",
    },
    consentLabel: "I agree to be contacted about my inquiry and to receive occasional marketing emails.",
    privacyLinkText: "Privacy Policy",
    submitLabel: "Send Inquiry",
    sendingLabel: "Sending…",
    successTitle: "Message received — thank you!",
    successMessage:
      "Your inquiry is safely with us. Paul or Laura will get back to you within one business day.",
    successCta: "Send another message",
  },

  footer: {
    tagline: "A family-run boutique AI agency. We deliver PLZ'ing work.",
    contactEmail: "hello@plzcompany.com",
    copyright: "PLZ Company. All rights reserved.",
  },

  privacy: {
    title: "Privacy Policy",
    updated: "Last updated: July 2026",
    sections: [
      {
        heading: "What we collect",
        body: "When you submit our contact form we collect your name, email address, optional company name, and your message, along with your marketing-consent choice.",
      },
      {
        heading: "How we use it",
        body: "We use your information to respond to your inquiry and, only if you opt in, to send occasional marketing communications. We never sell or rent your data.",
      },
      {
        heading: "Where it is stored",
        body: "Inquiries are stored in our secure database first, then shared with our team by email and logged in a private spreadsheet so no message is ever lost.",
      },
      {
        heading: "Your rights",
        body: "You can ask us to view, correct, or delete your information at any time by emailing hello@plzcompany.com.",
      },
    ],
  },
};

export default content;
